import { random } from "utils/helpers"
import { serverGetPendingMessages } from "utils/server"
import {
  ClientStudent,
  JoinRequestError,
  PendingMessagesResponse,
  ReconnectResponse,
  ReconnectTeacherResponse,
  ReviewScreenResponse,
  ScreenFinishedResponse,
  StartedResponse,
  StudentAddedResponse,
  StudentAnsweredResponse,
  ToggleTimerResponse,
} from "./play-model"
import { globalModifyPlayState } from "play/context/play-context"
import {
  applyElapsedSecondsToState,
  applyTimerToState,
  connectionClosedReducer,
  finishScreenReducer,
  gameStartedReducer,
  joinRequestErrorReducer,
  reconnectSuccessfulReducer,
  reviewRequestSucceededReducer,
  screenFinishedReducer,
  studentRemovedReducer,
  teacherReconnectSuccessfulReducer,
  pendingMessagesFailedReducer,
} from "play/reducers"

function randomPingInterval(hasTimer: boolean) {
  if (hasTimer) {
    return random(500, 600)
  } else {
    return random(1200, 2200)
  }
}

let s_getMessagesTimer: ReturnType<typeof setTimeout> | null = null
let s_loadingMessages = false
let s_failedRefreshCount = 0

export function startListeningForMessages(args: {
  firstTime: boolean
  hasTimer: boolean
}) {
  if (!!s_getMessagesTimer) {
    console.log("has timer, abort")
    clearTimeout(s_getMessagesTimer)
    s_getMessagesTimer = null
  }

  globalModifyPlayState((state) => {
    if (!args.firstTime) {
      const { isStudentRemoved } = state
      if (isStudentRemoved) {
        return console.log("startListeningForMessages, removed, abort")
      }
    } else {
      console.log("startListeningForMessages, first time")
    }

    s_getMessagesTimer = setTimeout(() => {
      checkForNewMessages(args.hasTimer)
      // cancel the timer and restart it after we get response from the server
      return 0
    }, randomPingInterval(args.hasTimer))
  })
}

function checkForNewMessages(hasTimer: boolean) {
  if (s_getMessagesTimer) {
    clearTimeout(s_getMessagesTimer)
    s_getMessagesTimer = null
  }

  globalModifyPlayState((state) => {
    const { connectionId, isStudentRemoved } = state

    if (isStudentRemoved) {
      return console.log("stop listening for messages: student removed")
    }
    if (s_loadingMessages) {
      return console.log("stop listening for messages: loading messages")
    }
    if (!connectionId) {
      return console.log("stop listening for messages: connection id is empty")
    }

    s_loadingMessages = true

    serverGetPendingMessages(connectionId)
      .then((resp) => processPendingMessages(resp, hasTimer))
      .catch((reason) => processPendingMessagesFailure(reason, hasTimer))
  })
}

function processPendingMessages(
  resp: PendingMessagesResponse,
  hasTimer: boolean
) {
  s_loadingMessages = false
  if (s_failedRefreshCount > 0) {
    s_failedRefreshCount = 0
    globalModifyPlayState((state) => {
      state.isReconnecting = false
    })
  }

  if (!!resp.error) {
    console.log("Broadcast error %s, stopping", resp.error)
    // store.dispatch(connectionClosed());
    return
  }

  globalModifyPlayState((state) => {
    for (let msg of resp.messages) {
      console.log("Got broadcast", msg.type, msg.data || "")

      switch (msg.type) {
        case "ReconnectFailed": {
          // alert("Reconnect failed")
          connectionClosedReducer(state)
          break
        }

        case "ReconnectSuccessful": {
          reconnectSuccessfulReducer(state, msg.data as ReconnectResponse)
          break
        }

        case "ReconnectTeacherSuccessful": {
          teacherReconnectSuccessfulReducer(
            state,
            msg.data as ReconnectTeacherResponse
          )
          break
        }

        case "UsernameTaken": {
          joinRequestErrorReducer(state, {
            error: JoinRequestError.usernameTaken,
          })
          break
        }

        case "SessionStarted": {
          state.sessionStarted = true
          break
        }

        case "StudentAdded": {
          const resp = msg.data as StudentAddedResponse
          const student = resp.student
          const totalStudents = resp.totalStudents

          console.log("studentAddedReducer", student)

          const idx = state.students.findIndex(
            (x) => x.connectionId === student.connectionId
          )
          if (idx === -1) {
            console.log("adding")
            state.students.push(student)
          } else {
            console.log("updating")
            state.students[idx] = student
          }

          const idxLobby = state.studentsInLobby.findIndex(
            (x) => x.connectionId === student.connectionId
          )

          if (idxLobby === -1) {
            state.studentsInLobby.push(student)
            student.hasJoined = true
          } else {
            state.studentsInLobby[idxLobby].hasJoined = true
          }

          state.countStartedStudents = totalStudents
          break
        }

        case "StudentRemoved": {
          studentRemovedReducer(state, msg.data as ClientStudent)
          break
        }

        case "Start": {
          const resp = msg.data as StartedResponse
          if (state.isTeacherUrl) {
            // console.log('Started event applies to students only');
            state.countAnsweredStudents = 0
            state.countStartedStudents = resp.countStartedStudents
            state.countScreens = resp.countScreens
            state.reportId = resp.reportId
            applyTimerToState(state, resp)
          } else {
            gameStartedReducer(state, resp)
          }
          break
        }

        case "ScreenFinished": {
          const resp = msg.data as ScreenFinishedResponse
          resp.isBroadcast = true
          screenFinishedReducer(state, resp)
          break
        }

        case "StudentAnswer": {
          const resp = msg.data as StudentAnsweredResponse
          state.countAnsweredStudents = resp.studentsCount
          break
        }

        case "NewReviewScreen": {
          reviewRequestSucceededReducer(state, msg.data as ReviewScreenResponse)
          break
        }

        case "TimerToggle": {
          const resp = msg.data as ToggleTimerResponse
          state.timer.secondsRemaining = resp.secondsRemaining
          state.timer.running = resp.isEnabled
          state.timer.visible = true
          state.timer.initialTimestamp = null

          applyElapsedSecondsToState(state, {
            timerSecondsRemaining: resp.secondsRemaining,
          })
          break
        }

        case "TimerTimedOut": {
          if (!state.isTeacherUrl) {
            finishScreenReducer(state, { timeout: true })
          }
          break
        }

        default: {
          console.error("Unknown broadcast message from server", msg)
          break
        }
      }
    }
  })

  startListeningForMessages({ firstTime: false, hasTimer })
}

// failed polls in a row before the player is told the connection is being
// retried; each poll has already been attempted three times by then
const FAILED_POLLS_BEFORE_RECONNECTING = 2

function processPendingMessagesFailure(reason: unknown, hasTimer: boolean) {
  s_loadingMessages = false

  globalModifyPlayState((state) => {
    const { connectionId, isStudentRemoved } = state

    console.warn(
      `Failed to get messages for ${connectionId}: ${reason || "<unknown>"}`
    )
    s_failedRefreshCount++
    if (!isStudentRemoved) {
      if (s_failedRefreshCount < 10) {
        if (s_failedRefreshCount >= FAILED_POLLS_BEFORE_RECONNECTING) {
          state.isReconnecting = true
        }
        startListeningForMessages({ firstTime: false, hasTimer })
      } else {
        console.warn("Giving up after %d attempts", s_failedRefreshCount)
        pendingMessagesFailedReducer(state, reason)
      }
    }
  })
}
